import { useStatsigClient, useFeatureGate } from '@statsig/react-bindings'
import isEmpty from 'lodash/fp/isEmpty'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import Router from 'next/router'
import { useContext, useEffect, useState, useMemo } from 'react'

import { formModalResultsLoaded } from 'clients/segment'
import {
  CardCartEXP148,
  CardCart,
  CookieDisclaimer,
  CreditModal,
  Footer,
  FooterMin,
  ModalLeaveBehindCard,
  ModalLeaveBehindContext,
  NavBar,
  SortSelect,
} from 'components/'
import { useAnonymousId } from 'components/app/anonymousIdContext'
import { useComparisonCartCards } from 'components/app/comparisonCartCardsContext'
import { MultistepBusinessForm } from 'components/business-modal'
import { MobileNav } from 'components/navigation/mobile-nav'
import cn from 'utils/classnames'
import { convertSearchParamsToObject } from 'utils/convertSearchParamsToObject'
import { useShouldIncreaseProductVisibilityExp384 } from 'utils/experimentation/useShouldIncreaseProductVisibility'
import { useEnhanceCompareCardsVisibility } from 'utils/experimentation/useEnhanceCompareCardsVisibility'
import { useQuestionForm } from 'utils/useQuestionForm'
import { getIsInternalUser } from 'utils/getIsInternalUser'
import type { CompareCredit } from '../../../types'
import { QuestionForm } from 'components/question-form'
import {
  BusinessSettings,
  CreditScoreFilter,
  Disclosure,
} from 'types/sanity.types'

let hasPageLoaded = false

const Navigation: React.FC<{
  alert: {
    height: number
    setHeight: React.Dispatch<React.SetStateAction<number>>
    active: boolean
    show: boolean
    setShowAlert: React.Dispatch<React.SetStateAction<boolean>>
    message: any[] | undefined
  }
  banner: React.ReactNode | undefined
  disclaimerAlignment?: 'left' | 'right' | 'top' | string
  hideAsSeenIn?: boolean
  hideBBBIcon?: boolean
  lightTheme?: boolean
  mixTheme?: boolean
  navNone?: boolean
  navType?: string
  navPosition?: string
  onHome: boolean
  setShowAlertUpsell: React.Dispatch<React.SetStateAction<boolean>>
  upsellBanner?: CompareCredit.NavUpsellBanner | null
  disclosure: Disclosure[]
}> = ({
  alert,
  banner,
  disclaimerAlignment,
  hideAsSeenIn,
  hideBBBIcon,
  lightTheme,
  mixTheme,
  onHome,
  navType,
  navPosition,
  setShowAlertUpsell,
  upsellBanner,
  disclosure,
}) => {
  const [showMobileNav, setShowMobileNav] = useState<boolean>(false)
  const [selectedSubMenu, setSelectedSubMenu] = useState<string | null>(null)

  return (
    <>
      <NavBar
        alert={alert}
        banner={banner}
        disclaimerAlignment={disclaimerAlignment}
        hideAsSeenIn={hideAsSeenIn}
        hideBBBIcon={hideBBBIcon}
        lightTheme={lightTheme}
        mixTheme={mixTheme}
        onHome={onHome}
        setShowMobileNav={setShowMobileNav}
        showMobileNav={showMobileNav}
        setSelectedSubMenu={setSelectedSubMenu}
        navType={navType}
        navPosition={navPosition}
        setShowAlertUpsell={setShowAlertUpsell}
        upsellBanner={upsellBanner}
        disclosure={disclosure}
      />
      <MobileNav
        alert={{
          height: alert.height,
          show: alert.show,
        }}
        show={showMobileNav}
        selectedSubMenu={selectedSubMenu}
        setSelectedSubMenu={setSelectedSubMenu}
        setShowMobileNav={setShowMobileNav}
      />
    </>
  )
}

export const LayoutMain: React.FC<
  React.PropsWithChildren<{
    alert?: {
      active: boolean
      message: any[] | undefined
    }
    banner?: React.ReactNode
    comparisonDisabled?: boolean
    disclaimerAlignment?: 'left' | 'right' | 'top' | string
    extraClasses?: string
    footerType?: string
    hideAsSeenIn?: boolean
    hideBBBIcon?: boolean
    lightTheme?: boolean
    mixTheme?: boolean
    mobileFooterTileHeight?: number
    navNone?: boolean
    navType?: string
    navPosition?: string
    onHome?: boolean
    showBackButton?: boolean
    hideBackButtonInComparisionPage?: boolean
    upsellBanner?: CompareCredit.NavUpsellBanner | null
    modalLeaveBehindCard?: CompareCredit.ModalLeaveBehindCard
    isModalLeaveBehindCard?: boolean
    slug?: string
    creditScoreFilter: CreditScoreFilter
    disclosure: Disclosure[]
    businessSettings: BusinessSettings
  }>
> = ({
  alert = { active: false, message: undefined },
  disclaimerAlignment,
  extraClasses,
  navNone,
  isModalLeaveBehindCard,
  modalLeaveBehindCard,
  upsellBanner,
  banner,
  hideAsSeenIn,
  hideBBBIcon,
  lightTheme,
  mixTheme,
  navType,
  navPosition,
  onHome,
  children,
  footerType,
  hideBackButtonInComparisionPage,
  showBackButton,
  comparisonDisabled,
  creditScoreFilter,
  disclosure,
  businessSettings,
}) => {
  const isInternalUser = useMemo(() => getIsInternalUser(), [])

  const [showAlert, setShowAlert] = useState<boolean>(false)
  const [alertHeight, setAlertHeight] = useState<number>(0)
  const [showAlertUpsell, setShowAlertUpsell] = useState<boolean>(false)
  const [showBanner, setShowBanner] = useState(false)
  const urlQueryParamsObject = convertSearchParamsToObject(
    new URL(window.location.href).searchParams,
  )
  const pathname = usePathname()
  const { logEvent } = useStatsigClient()

  const anonymousId = useAnonymousId()
  const userContext = useMemo(() => ({ anonymousId }), [anonymousId])
  const { comparisonCartCards: comparisonCart } = useComparisonCartCards()

  const { questions } = useQuestionForm({
    slug: urlQueryParamsObject.formModal,
  })

  // show/hide alert when alert.active changes
  // this should force a change every time a new category is selected
  useEffect(() => setShowAlert(alert.active), [alert.active])

  useEffect(() => {
    // Allows us to ensure we only log the page_event one time
    // LayoutMain currently re-renders so a typical [] useEffect
    // does not work by itself.
    if (!hasPageLoaded) {
      hasPageLoaded = true
      logEvent(
        'page_loaded',
        pathname || undefined,
        urlQueryParamsObject || undefined,
      )
    }
  }, [])

  useEffect(() => {
    if (
      urlQueryParamsObject.fromModalRedirect !== 'true' ||
      !urlQueryParamsObject.originPath
    )
      return

    setShowBanner(true)

    formModalResultsLoaded({
      page_path: urlQueryParamsObject.originPath,
      user_context: userContext,
      lp_url: `${window.location.origin}${pathname}`,
    })

    const url = new URL(window.location.href)
    url.searchParams.delete('fromModalRedirect')
    url.searchParams.delete('originPath')
    window.history.replaceState({}, '', url.toString())
  }, [urlQueryParamsObject, userContext])

  const { value: showSortSelect } = useFeatureGate('select_sort')

  /* ========== LBM ========== */
  const {
    modalLeaveBehindOrderId,
    openModalLeaveBehind,
    onCloseModalLeaveBehind,
  } = useContext(ModalLeaveBehindContext)

  const externalId = '00'
  const categoryId = '00'

  // 2025.06: EXP-384 Slim Category Design
  const shouldIncreaseProductVisibility =
    useShouldIncreaseProductVisibilityExp384()

  // 2025.06: Exp 148 : Enhance Compare Cards Visibility (Add Card & Cart)
  const shouldEnhanceCompareCardsVisibility = useEnhanceCompareCardsVisibility()

  const renderedCardCart = useMemo(() => {
    return shouldEnhanceCompareCardsVisibility ? (
      <CardCartEXP148 />
    ) : (
      <CardCart />
    )
  }, [shouldEnhanceCompareCardsVisibility])

  return (
    <>
      <div
        id="layout-main"
        className={cn(
          'c-layout-main flex flex-col min-h-screen justify-between ',
          extraClasses,
        )}
      >
        {isInternalUser && (
          <div className="fixed bottom-4 right-4 z-50">
            <div className="bg-amber-600 text-white text-sm p-4 rounded-lg shadow-lg border border-gray-300 animate-slide-in">
              Segment is off
            </div>
          </div>
        )}
        <Link
          className="c-skip-link / sr-only focus:not-sr-only"
          href="#main-content"
        >
          Skip to main content
        </Link>
        {navNone ? (
          ''
        ) : (
          <Navigation
            alert={{
              active: alert.active,
              height: alertHeight,
              message: alert.message,
              setHeight: setAlertHeight,
              setShowAlert: setShowAlert,
              show: showAlert,
            }}
            banner={banner}
            disclaimerAlignment={disclaimerAlignment}
            hideAsSeenIn={hideAsSeenIn}
            hideBBBIcon={hideBBBIcon}
            lightTheme={lightTheme}
            mixTheme={mixTheme}
            navType={navType}
            navPosition={navPosition}
            onHome={onHome || false}
            setShowAlertUpsell={setShowAlertUpsell}
            upsellBanner={upsellBanner}
            disclosure={disclosure}
          />
        )}
        {showSortSelect && <SortSelect />}
        <div
          className={cn(`c-main__container transition-25s`, {
            'pt-40 xs:pt-36 sm:pt-[7.5rem] lg:pt-[8.5rem]': showAlertUpsell,
            'pt-14 lg:pt-16':
              !showAlertUpsell && shouldIncreaseProductVisibility,
            'pt-16 lg:pt-20':
              !showAlertUpsell && !shouldIncreaseProductVisibility,
            'c-main__container--disclaimer-top': disclaimerAlignment === 'top',
            'pt-0 lg:pt-0': navPosition === 'non-sticky',
          })}
          style={{
            marginTop: showAlert ? alertHeight : 0,
          }}
        >
          <main
            id="main-content"
            className={`c-main / container-fluid text-dark ${
              showSortSelect && 'c-sort-select-desktop'
            }`}
          >
            {showBanner && (
              <div className="relative my-2 py-2 px-4 bg-primary-light-bg rounded-sm border border-primary-light">
                <p className="mr-4 text-center text-card-gray">
                  These are the best products for you based on your answers.
                </p>
                <button
                  className="absolute top-0 md:top-1 right-0 flex justify-center w-8 h-8"
                  onClick={() => setShowBanner(false)}
                >
                  <Image
                    src="/static/icons/icon-close-black.svg"
                    alt="close icon"
                    width={16}
                    height={16}
                    className="opacity-40"
                  />
                </button>
              </div>
            )}
            {children}
          </main>{' '}
        </div>
        <CookieDisclaimer />
        {footerType === 'minimal' ? (
          <FooterMin disclosure={disclosure} />
        ) : (
          <Footer disclosure={disclosure} />
        )}
        <CreditModal creditScoreFilter={creditScoreFilter} />
        {showBackButton && !hideBackButtonInComparisionPage && (
          <div className="c-cart is-on / flex w-full / fixed z-30 bottom-0 left-0 / sm:ml-21 sm:mb-21 / transition-all--25">
            <div className="c-cart__row / relative / flex content-start items-center justify-start / w-full / px-4 py-2  lg:px-4 / bg-white shadow-2xl">
              <button
                className="c-cart__btn-reset flex items-center / w-16 h-10 pr-4 / text-gray-600 text-sm sm:text-base / hover:text-primary-bright focus:outline-none / transition-all"
                onClick={() => Router.back()}
              >
                <div className="shrink-0 inline-block mr-2 / opacity-50 / w-4 h-4">
                  <Image
                    alt="chevron left"
                    src="/static/icons/icon-chevron-l-black.svg"
                    height={16}
                    width={16}
                  />
                </div>
                Back
              </button>
            </div>
          </div>
        )}
      </div>

      <MultistepBusinessForm businessSettings={businessSettings} />

      {isModalLeaveBehindCard && (
        <div className="relative z-80">
          {modalLeaveBehindOrderId &&
            modalLeaveBehindCard &&
            modalLeaveBehindCard.referencedCards &&
            modalLeaveBehindCard.cardOrderIds && (
              <ModalLeaveBehindCard
                modalLeaveBehindCardProduct={
                  modalLeaveBehindCard.referencedCards[
                    modalLeaveBehindCard.modalLeaveBehindCardProduct.slug
                  ] as CompareCredit.FormattedCard
                }
                modalLeaveBehindCardTitle={
                  modalLeaveBehindCard.modalLeaveBehindCardTitle
                }
                modalLeaveBehindCardContent={
                  modalLeaveBehindCard.modalLeaveBehindCardContent
                }
                modalLeaveBehindCardDeclineText={
                  modalLeaveBehindCard.modalLeaveBehindCardDeclineText
                }
                categoryId={categoryId}
                externalId={externalId}
                orderId={modalLeaveBehindOrderId}
                cardOrderIds={modalLeaveBehindCard.cardOrderIds}
                referencedCards={modalLeaveBehindCard.referencedCards}
                open={openModalLeaveBehind}
                onClose={onCloseModalLeaveBehind}
              />
            )}
        </div>
      )}

      {questions && <QuestionForm questions={questions} />}

      {!showBackButton ? (
        renderedCardCart
      ) : (
        <>
          {!comparisonDisabled && !isEmpty(comparisonCart) && renderedCardCart}
        </>
      )}

      <style jsx>{`
        /* T-DISCLAIMER-TOP: Theme used to display the advertiser disclosure
        at the top of the tips pages
        ======================================================= */
        .c-main__container--disclaimer-top {
          padding-top: 5.5rem;
        }
        @media (min-width: 1024px) {
          .c-main__container--disclaimer-top {
            padding-top: 6.5rem;
          }
        }
      `}</style>
    </>
  )
}
